import { AccountSubtype } from "@common/constants/domains/accounting/accounts";
import { isAccountSubtypeValidForType } from "@common/constants/domains/accounting/accountTypeHelpers";
import { getAccountClassForType } from "@common/constants/domains/accounting/helpers";
import {
    countImportedAccountMappingResolutions,
    getImportedAccountMappingResolution,
    ImportedAccountMappingResolution,
} from "@common/helpers/importedAccountMappingResolution";
import { asIsSourceCodeText } from "@common/helpers/importedAccountSourceNumber";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { Account } from "@common/types/domains/accounting/account";
import { ExtendedAccount } from "@common/types/reports/types";
import { resolveMappedKickAccount } from "../../resolveMappedKickAccount";

export interface CoaMappingPreviewSource {
    id: string;
    name: string;
    sourceNumber: string | null;
}

export interface CoaMappingPreview {
    accounts: ExtendedAccount[];
    sourcesByAccountId: Map<string, CoaMappingPreviewSource[]>;
    newAccountIds: Set<string>;
    totalAccountCount: number;
}

interface BuildCoaMappingPreviewParams {
    importedAccounts: ImportedAccount[];
    kickAccounts: Account[];
    entityId: number;
}

export const NEW_PREVIEW_ACCOUNT_ID_PREFIX = "new:";

// Finalization reuses an ungrouped account with the same name and type
// instead of creating one (account-name-collision.helper.ts); flagged rows
// are matched only against the chart, never against each other.
function nameMatchKey(account: Pick<Account, "name" | "type">): string {
    return `${account.name.trim().toLowerCase()}:${account.type}`;
}

function toPreviewSource(account: ImportedAccount): CoaMappingPreviewSource {
    return {
        id: account.id,
        name: account.name,
        sourceNumber: asIsSourceCodeText(account),
    };
}

function toExtendedAccount(
    account: Account,
    entityId: number,
): ExtendedAccount {
    return {
        ...account,
        entityId,
        debit: 0,
        credit: 0,
        balance: 0,
        hasOpeningBalance: false,
    };
}

function buildNewPreviewAccount(
    importedAccount: ImportedAccount,
    entityId: number,
): ExtendedAccount | null {
    const type = importedAccount.kickType;
    if (type === null) {
        return null;
    }
    const subtype =
        importedAccount.kickSubtype ??
        Object.values(AccountSubtype).find((candidate) =>
            isAccountSubtypeValidForType(candidate, type),
        ) ??
        AccountSubtype.cashOnHand;
    return {
        id: `${NEW_PREVIEW_ACCOUNT_ID_PREFIX}${importedAccount.id}`,
        code: Number.MAX_SAFE_INTEGER,
        codeText: null,
        name: importedAccount.name,
        class: getAccountClassForType(type),
        type,
        subtype,
        isDisabled: false,
        isManuallyCreated: true,
        financialAccountId: null,
        kickOriginalCode: null,
        parentGroupId: null,
        sortOrder: Number.MAX_SAFE_INTEGER,
        entityId,
        debit: 0,
        credit: 0,
        balance: 0,
        hasOpeningBalance: false,
    };
}

export function buildCoaMappingPreview({
    importedAccounts,
    kickAccounts,
    entityId,
}: BuildCoaMappingPreviewParams): CoaMappingPreview {
    const kickAccountsById = Object.fromEntries(
        kickAccounts.map((account) => [account.id, account]),
    );
    const accounts = kickAccounts.map((account) =>
        toExtendedAccount(account, entityId),
    );
    const accountIdByNameMatchKey = new Map<string, string>();
    for (const account of kickAccounts) {
        const key = nameMatchKey(account);
        if (
            account.parentGroupId === null &&
            !accountIdByNameMatchKey.has(key)
        ) {
            accountIdByNameMatchKey.set(key, account.id);
        }
    }
    const sourcesByAccountId = new Map<string, CoaMappingPreviewSource[]>();
    const newAccountIds = new Set<string>();

    const addSource = (accountId: string, importedAccount: ImportedAccount) => {
        const sources = sourcesByAccountId.get(accountId) ?? [];
        sources.push(toPreviewSource(importedAccount));
        sourcesByAccountId.set(accountId, sources);
    };

    for (const importedAccount of importedAccounts) {
        const resolution = getImportedAccountMappingResolution({
            account: importedAccount,
        });
        if (resolution === ImportedAccountMappingResolution.EXISTING_ACCOUNT) {
            const kickAccount = resolveMappedKickAccount({
                account: importedAccount,
                kickAccountsById,
            });
            if (kickAccount) {
                addSource(kickAccount.id, importedAccount);
            }
            continue;
        }
        if (resolution === ImportedAccountMappingResolution.NEW_ACCOUNT) {
            const newAccount = buildNewPreviewAccount(
                importedAccount,
                entityId,
            );
            if (!newAccount) {
                continue;
            }
            const matchKey = nameMatchKey(newAccount);
            const matchedAccountId = accountIdByNameMatchKey.get(matchKey);
            if (matchedAccountId !== undefined) {
                addSource(matchedAccountId, importedAccount);
                continue;
            }
            accounts.push(newAccount);
            newAccountIds.add(newAccount.id);
            addSource(newAccount.id, importedAccount);
        }
    }

    const counts = countImportedAccountMappingResolutions({
        accounts: importedAccounts,
    });

    return {
        accounts,
        sourcesByAccountId,
        newAccountIds,
        totalAccountCount: accounts.length + counts.connectInTasks,
    };
}
